<template>
  <e-chart
    :key="chartKey"
    class="oscillography-alarm-chart"
    autoresize
    :style="{ height: chartHeight }"
    :options="chartOptions"
  />
</template>

<script>
import themeService from '@/services/themeService';
import {
  generate,
  formatPercentage,
} from '@/domain/services/charts/barsBuilderOscillography';

export default {
  name: 'OscillographyAlarmChart',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    alarmMode: {
      type: String,
      default: 'breaker',
    },
    persistenceMode: {
      type: String,
      default: 'percentage',
    },
    phase: {
      type: String,
      default: '',
    },
  },
  computed: {
    chartKey() {
      const values = this.rows.flatMap(row => [
        row.name,
        row.opening?.count,
        row.opening?.total,
        row.closing?.count,
        row.closing?.total,
        row.motor?.count,
        row.motor?.total,
      ]);

      return [this.alarmMode, this.persistenceMode, this.phase, ...values].join('|');
    },
    chartHeight() {
      const ROW_HEIGHT_REM = 5;
      return `${Math.max(36, this.rows.length * ROW_HEIGHT_REM)}rem`;
    },
    chartRows() {
      return this.rows.map(row => ({
        ...row,
        label: this.alarmName(row),
      }));
    },
    chartOptions() {
      return generate(this.chartRows, {
        alarmMode: this.alarmMode,
        persistenceMode: this.persistenceMode,
        labels: {
          title: this.$t('engineering.oscillography-analysis.alarms.persistence.title'),
          closing: this.closingLabel,
          opening: this.openingLabel,
          motor: this.motorLabel,
        },
        tooltipFormatter: this.chartTooltip,
        fontColor: this.fontColor,
        secondaryFontColor: this.secondaryFontColor,
        gridColor: this.gridColor,
        tooltipBackgroundColor: this.tooltipBackgroundColor,
        tooltipBorderColor: themeService.getStyleVar('color-tooltip-border'),
        locale: this.$i18n?.locale || 'pt-BR',
      });
    },
    closingLabel() {
      return this.$t('engineering.oscillography-analysis.alarms.persistence.closing');
    },
    openingLabel() {
      return this.$t('engineering.oscillography-analysis.alarms.persistence.opening');
    },
    motorLabel() {
      return this.$t('engineering.oscillography-analysis.alarms.persistence.motor');
    },
    fontColor() {
      return themeService.getFontColor();
    },
    secondaryFontColor() {
      return themeService.getStyleVar('font-color-secondary');
    },
    gridColor() {
      return themeService.getStyleVar('color-border-inner') || 'rgba(255, 255, 255, .12)';
    },
    tooltipBackgroundColor() {
      return themeService.getStyleVar('color-tooltip-background')
        || themeService.getStyleVar('color-background-regular')
        || '#3d4143';
    },
  },
  methods: {
    alarmName(row) {
      const variableKey = row.name?.startsWith('variables.')
        ? row.name.substring('variables.'.length)
        : null;
      const translationKey = variableKey
        ? `engineering.oscillography-analysis.variables.${variableKey}`
        : `engineering.oscillography-analysis.alarms.persistence.names.${row.name}`;

      return this.$t(translationKey);
    },
    valueFor(row, side) {
      return row[side];
    },
    maneuverTypeLabel(side) {
      if (side === 'closing') return this.closingLabel;
      if (side === 'motor') return this.motorLabel;
      return this.openingLabel;
    },
    chartTooltip(params) {
      const points = Array.isArray(params) ? params : [params];
      const firstPoint = points[0];
      if (!firstPoint) return '';

      const row = this.chartRows[firstPoint.dataIndex];
      if (!row) return '';

      const values = this.alarmMode === 'motor'
        ? [{ side: 'motor', value: this.valueFor(row, 'motor') }]
        : [
          { side: 'closing', value: this.valueFor(row, 'closing') },
          { side: 'opening', value: this.valueFor(row, 'opening') },
        ];
      const tooltipBase = 'engineering.oscillography-analysis.alarms.persistence.tooltip';
      const lines = [
        `<b>${this.$t(`${tooltipBase}.name`)}: ${this.alarmName(row)}</b>`,
      ];

      values.forEach(({ side, value }) => {
        const maneuver = this.maneuverTypeLabel(side);
        const quantity = this.$t(`${tooltipBase}.quantity`);
        const percentage = this.$t(`${tooltipBase}.percentage`);
        const valueText = `${value.count}/${value.total}`;
        lines.push(`${this.$t(`${tooltipBase}.maneuver`)} (${maneuver}): ${quantity} ${valueText} (${percentage}: ${formatPercentage(value.percentage, this.$i18n?.locale || 'pt-BR')}%)`);
      });

      lines.push(`${this.$t(`${tooltipBase}.phase`)}: ${this.phase}`);
      return lines.join('<br/>');
    },
  },
};
</script>

<style lang="sass" scoped>
.oscillography-alarm-chart
  width: 100%
  min-height: 28rem
</style>
